import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form } from '@ant-design/compatible';
import { Input, Select, Button, message, Alert, Tooltip } from 'antd';
import Avatar from 'BugHerdClient/shared/Avatar';
import sortUsers from '../../utils/sortUsers';
import { X as Close, Mail } from 'lucide-react';
import styles from './index.module.css';
import Loader from 'components/loader';
import { throttle } from 'lodash-es';
import ArrowUp from './../../../../clients/sidebar/assets/images/ic-upgrade-circle.svg';
import UpgradePopover from 'BugHerdClient/sidebar/components/UpgradePopover';
import ssoPopoverHeaderImage from './images/img-sso-upgrade-tooltip.png';
import { InviteProps, InviteRole, PendingInvite } from './types';
import {
  canUseMemberRole,
  groupInvitesByRole,
  isMemberOptionDisabled,
  normalizeEmail,
  parseEmailInput,
  remainingSeats,
  roleForNewOrgInvite
} from './helpers';

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const Invite = ({
  users: initialUsers,
  role: roleFromProps,
  membersMax,
  membersCount,
  collaboratorsEnabled,
  project,
  endpoint,
  siteId,
  assetId,
  success,
  authenticityToken,
  responseError,
  onInvitedUser,
  container,
  billingRights,
  isShareDeliverable,
  orgId
}: InviteProps) => {
  const multiInvite = !!project;

  const computeInitialRole = (): InviteRole =>
    roleForNewOrgInvite(
      roleFromProps,
      !!project,
      [],
      membersMax,
      membersCount
    );

  const [saving, setSaving] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState('');
  const [defaultRole, setDefaultRole] = useState<InviteRole>(
    computeInitialRole
  );
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState(initialUsers || []);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);

  const lastFetchIdRef = useRef(0);
  const isFirstRender = useRef(true);
  const composerInputRef = useRef<HTMLInputElement | null>(null);
  const blurSuggestionsTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setDefaultRole(
      roleForNewOrgInvite(
        roleFromProps,
        !!project,
        pendingInvites,
        membersMax,
        membersCount
      )
    );
  }, [roleFromProps, pendingInvites, membersMax, membersCount, project]);

  const intendedNewInviteRole = () =>
    roleForNewOrgInvite(
      roleFromProps,
      !!project,
      pendingInvites,
      membersMax,
      membersCount
    );

  const doSearch = useRef(
    throttle((searchVal: string, fetchUrl: string) => {
      setLoading(true);
      lastFetchIdRef.current++;
      const fetchId = lastFetchIdRef.current;
      const query =
        searchVal.length > 0 ? `?search=${encodeURIComponent(searchVal)}` : '';
      fetch(`${fetchUrl}${query}`)
        .then(res => res.json())
        .then(res => {
          if (fetchId === lastFetchIdRef.current) {
            setLoading(false);
            setUsers(res.users);
          }
        });
    }, 1000)
  ).current;

  const emailValidation = (value = '') =>
    (value.match(/@/g) || []).length === 1;

  const existingUser = users.find(
    u => normalizeEmail(u.email) === normalizeEmail(email)
  );

  const buildInvite = useCallback(
    (emailValue: string, role: InviteRole): PendingInvite | null => {
      const trimmed = (emailValue ?? '').trim();
      const normalized = normalizeEmail(trimmed);

      if (!emailRegex.test(trimmed)) return null;

      const matchedUser = users.find(
        u => normalizeEmail(u.email) === normalized
      );

      if (matchedUser && !project) {
        if (matchedUser.role !== 'guest' || role === 'member') {
          return null;
        }
      }

      let inviteRole = role;
      if (matchedUser) {
        inviteRole =
          matchedUser.role === 'collaborator' || matchedUser.role === 'guest'
            ? matchedUser.role
            : 'member';
      } else if (project) {
        if (role === 'member') {
          inviteRole = canUseMemberRole(
            { existingOrgRole: undefined },
            pendingInvites,
            membersMax,
            membersCount
          )
            ? 'member'
            : 'guest';
        } else {
          inviteRole = role;
        }
      } else if (
        role === 'member' &&
        !canUseMemberRole(
          { existingOrgRole: undefined },
          pendingInvites,
          membersMax,
          membersCount
        )
      ) {
        inviteRole = 'collaborator';
      }

      return {
        key: normalized,
        email: matchedUser?.email ?? trimmed,
        role: inviteRole,
        userId: matchedUser?.id,
        displayName: matchedUser?.display_name,
        existingOrgRole: matchedUser?.role
      };
    },
    [users, project, pendingInvites, membersMax, membersCount]
  );

  const removePendingInvite = (key: string) => {
    setPendingInvites(current => current.filter(invite => invite.key !== key));
  };

  const updatePendingInviteRole = (key: string, role: InviteRole) => {
    setPendingInvites(current => {
      const target = current.find(invite => invite.key === key);
      if (!target) return current;

      if (
        role === 'member' &&
        !canUseMemberRole(target, current, membersMax, membersCount, key)
      ) {
        return current;
      }

      const updated = current.map(invite =>
        invite.key === key ? { ...invite, role } : invite
      );

      if (isMemberOptionDisabled(updated, membersMax, membersCount)) {
        setDefaultRole(project ? 'guest' : 'collaborator');
      }

      return updated;
    });
  };

  const focusComposerInput = () => {
    composerInputRef.current?.focus?.();
  };

  const addToQueue = (input = email, roleOverride?: InviteRole) => {
    const emails = parseEmailInput(input);
    if (emails.length === 0) return false;

    const role = roleOverride ?? intendedNewInviteRole();
    const nextInvites: PendingInvite[] = [];
    const skipped: string[] = [];

    emails.forEach(emailValue => {
      const invite = buildInvite(emailValue, role);
      if (!invite) {
        skipped.push(emailValue);
        return;
      }

      if (
        pendingInvites.some(item => item.key === invite.key) ||
        nextInvites.some(item => item.key === invite.key)
      ) {
        return;
      }

      if (
        invite.role === 'member' &&
        !canUseMemberRole(
          invite,
          [...pendingInvites, ...nextInvites],
          membersMax,
          membersCount
        )
      ) {
        skipped.push(emailValue);
        return;
      }

      nextInvites.push(invite);
    });

    if (nextInvites.length > 0) {
      const updatedInvites = [...pendingInvites, ...nextInvites];
      setPendingInvites(updatedInvites);
      setEmail('');
      setInputError(null);
      setDefaultRole(
        roleForNewOrgInvite(
          roleFromProps,
          !!project,
          updatedInvites,
          membersMax,
          membersCount
        )
      );

      requestAnimationFrame(focusComposerInput);
    }

    if (skipped.length > 0) {
      const allInvalid = skipped.every(value => !emailRegex.test(value));
      setInputError(
        allInvalid
          ? skipped.length === 1
            ? 'This is not a valid email address.'
            : 'One or more email addresses are invalid.'
          : skipped.length === 1
          ? `Could not add ${skipped[0]}. Check the email or member seat limit.`
          : `Could not add ${skipped.length} addresses. Check emails or member seat limit.`
      );
    }

    return nextInvites.length > 0;
  };

  const onEmailChange = (value?: string) => {
    const nextEmail = value ?? '';
    if (project) doSearch(nextEmail, endpoint.fetchUsers ?? '');

    if (multiInvite && /[,;]\s*$/.test(nextEmail)) {
      const emails = parseEmailInput(nextEmail).filter(part =>
        emailRegex.test(part)
      );
      if (emails.length > 0) {
        addToQueue(emails.join(','));
        return;
      }
    }

    const matchedUser = users.find(
      u => normalizeEmail(u.email) === normalizeEmail(nextEmail)
    );

    let nextRole: InviteRole;
    if (matchedUser) {
      nextRole =
        matchedUser.role === 'collaborator' || matchedUser.role === 'guest'
          ? matchedUser.role
          : 'member';
    } else {
      nextRole = intendedNewInviteRole();
    }

    setEmail(nextEmail);
    setInputError(null);
    setIsValid(true);
    setDefaultRole(nextRole);
  };

  const buildFormData = (
    emailAddresses: string,
    role: InviteRole,
    userId?: number
  ) => {
    const data: [string, string][] = [
      ['utf8', '✓'],
      ['role', role],
      ['email_addresses', emailAddresses],
      ['send_email', '1']
    ];
    if (siteId) data.push(['site_id', String(siteId)]);
    if (assetId) data.push(['asset_id', String(assetId)]);
    if (userId) data.push(['user_id', String(userId)]);

    const formData = new FormData();
    data.forEach(([key, val]) => formData.append(key, val));
    return formData;
  };

  const submitInvites = async (invites: PendingInvite[]) => {
    const groups = groupInvitesByRole(invites);
    const invitedUsers: any[] = [];

    for (const [role, roleInvites] of Array.from(groups.entries())) {
      const response = await fetch(endpoint.sendEmail, {
        method: 'POST',
        body: buildFormData(
          roleInvites.map(invite => invite.email).join(','),
          role
        ),
        credentials: 'include',
        headers: { 'X-CSRF-Token': authenticityToken }
      });

      if (response.status !== 200 && response.status !== 302) {
        throw new Error(response.statusText);
      }

      const data = await response.json();
      invitedUsers.push(...data);
    }

    return invitedUsers;
  };

  const sendSingleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    const valid = emailValidation(email);
    if (!valid) {
      setIsValid(false);
      return;
    }

    setSaving(true);
    setIsValid(valid);

    try {
      const response = await fetch(endpoint.sendEmail, {
        method: 'POST',
        body: buildFormData(email, defaultRole, existingUser?.id),
        credentials: 'include',
        headers: { 'X-CSRF-Token': authenticityToken }
      });

      if (response.status !== 200 && response.status !== 302) {
        throw new Error(response.statusText);
      }

      const data = await response.json();
      setSaving(false);
      setIsValid(false);
      setEmail('');

      data.forEach((user: any) => {
        onInvitedUser?.(user);
        message.success(email + success);
      });
    } catch (error) {
      setSaving(false);
      message.error(
        error.message === 'Unprocessable Content'
          ? 'Member limit reached'
          : responseError ?? 'Something went wrong, please try again'
      );
    }
  };

  const sendMultiInvite = async (event?: React.FormEvent) => {
    event?.preventDefault();

    if ((email ?? '').trim()) {
      addToQueue();
      return;
    }
    if (pendingInvites.length === 0) return;

    setSaving(true);
    setInputError(null);

    try {
      const invitedUsers = await submitInvites(pendingInvites);
      setSaving(false);
      setEmail('');
      setPendingInvites([]);

      invitedUsers.forEach(user => onInvitedUser?.(user));

      const count = invitedUsers.length;
      message.success(
        count === 1
          ? `${invitedUsers[0].email}${success}`
          : `${count} people${success?.replace(
              'has been invited',
              'have been invited'
            ) ?? ' invited'}`
      );
    } catch (error) {
      setSaving(false);
      message.error(
        error.message === 'Unprocessable Content'
          ? 'Member limit reached'
          : responseError ?? 'Something went wrong, please try again'
      );
    }
  };

  const sendEmail = multiInvite ? sendMultiInvite : sendSingleInvite;

  const filterAutoComplete = (query: string) => {
    const normalizedQuery = normalizeEmail(query);
    let results = users.filter(
      user =>
        user.email &&
        !project.usersOnProject.some((pu: any) => pu.id === user.id) &&
        !pendingInvites.some(
          invite => invite.key === normalizeEmail(user.email)
        )
    );
    if (normalizedQuery) {
      results = results.filter(user => {
        const userEmail = normalizeEmail(user.email);
        const displayName = normalizeEmail(user.display_name);
        return (
          userEmail.includes(normalizedQuery) ||
          displayName.includes(normalizedQuery)
        );
      });
    }
    // @ts-expect-error sortUsers second param is optional at runtime
    return sortUsers(results.slice(0, 50));
  };

  const seatsLeft = remainingSeats(membersMax, membersCount);
  const memberLimitReached = membersMax != null && seatsLeft <= 0;
  const canNoLongerInviteAnyone = memberLimitReached && !collaboratorsEnabled;
  const isProjectActive = project?.show_on_live !== false;
  const showRoleSelect = !multiInvite && collaboratorsEnabled;
  const hasRemainingMembers = !memberLimitReached;
  const isValidEmailFormat = emailRegex.test((email ?? '').trim());
  const getIsValid = (isValid || email === '') && (project || !existingUser);

  const memberOptionDisabled = isMemberOptionDisabled(
    pendingInvites,
    membersMax,
    membersCount,
    existingUser?.role
  );

  const handleRoleChange = (role: InviteRole) => {
    if (role === 'member' && memberOptionDisabled) return;
    setDefaultRole(role);
  };

  const isSingleInviteDisabled =
    !isValidEmailFormat ||
    (!hasRemainingMembers && defaultRole === 'member' && !existingUser) ||
    !!(existingUser && !project);

  const helpMessage = () => {
    if (multiInvite && inputError) return inputError;
    if (!multiInvite && !(isValid || email === '')) {
      return 'This is not a valid email address.';
    }
    if (existingUser && !project) {
      if (existingUser.role === 'guest' && defaultRole === 'member') {
        return null;
      }
      return `${existingUser.display_name} is already a user on this Organization.`;
    }
    return null;
  };

  const guestToMemberInvites = pendingInvites.filter(
    invite => invite.existingOrgRole === 'guest' && invite.role === 'member'
  );
  const showGuestToMemberWarning = multiInvite
    ? guestToMemberInvites.length > 0
    : existingUser?.role === 'guest' && defaultRole === 'member';

  const messaging = isShareDeliverable
    ? 'Send this to anyone in the project team, or invite new Clients / Members to this project to view this deliverable.'
    : 'Add existing Team Members or invite new Members & Clients to this project';

  const totalToInvite =
    pendingInvites.length +
    ((email ?? '').trim() && isValidEmailFormat ? 1 : 0);

  const findUserForInvite = (invite: PendingInvite) =>
    users.find(
      user =>
        (invite.userId != null && user.id === invite.userId) ||
        normalizeEmail(user.email) === invite.key
    );

  const chipMemberOptionDisabled = (invite: PendingInvite) =>
    !canUseMemberRole(
      invite,
      pendingInvites,
      membersMax,
      membersCount,
      invite.key
    );

  const memberLimitTooltip =
    "You've reached the maximum number of members on your plan.";

  const memberOptionLabel = (disabled: boolean) =>
    disabled ? (
      <Tooltip
        title={memberLimitTooltip}
        getPopupContainer={container}
        placement="left"
      >
        <span className={styles.disabledMemberOption}>Member</span>
      </Tooltip>
    ) : (
      'Member'
    );

  const roleSelect = showRoleSelect && (
    <Form.Item className={styles.inviteRoleWrapper}>
      <Select
        className={styles.inviteRole}
        size="large"
        getPopupContainer={container}
        value={defaultRole}
        onChange={handleRoleChange}
        dropdownMatchSelectWidth={false}
        disabled={!isProjectActive}
        {...({ name: 'role' } as Record<string, unknown>)}
      >
        <Select.Option value="member" disabled={memberOptionDisabled}>
          {memberOptionLabel(memberOptionDisabled)}
        </Select.Option>
        {collaboratorsEnabled && (
          <Select.Option value="collaborator">Collaborator</Select.Option>
        )}
      </Select>
    </Form.Item>
  );

  const selectSuggestedUser = (user: any) => {
    let role: InviteRole = defaultRole;
    if (user.role === 'collaborator' || user.role === 'guest') {
      role = user.role;
    } else {
      role = 'member';
    }
    addToQueue(user.email, role);
    setComposerFocused(true);
    requestAnimationFrame(focusComposerInput);
  };

  const handleComposerKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (!multiInvite) return;

    if (event.key === 'Backspace' && !(email ?? '').trim()) {
      if (pendingInvites.length === 0) return;
      event.preventDefault();
      removePendingInvite(pendingInvites[pendingInvites.length - 1].key);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if ((email ?? '').trim()) {
        addToQueue();
      }
    }
  };

  const handleComposerFocus = () => {
    if (blurSuggestionsTimeoutRef.current) {
      clearTimeout(blurSuggestionsTimeoutRef.current);
      blurSuggestionsTimeoutRef.current = null;
    }
    setComposerFocused(true);
  };

  const handleComposerBlur = () => {
    blurSuggestionsTimeoutRef.current = setTimeout(() => {
      setComposerFocused(false);
    }, 150);
  };

  const suggestions = multiInvite ? filterAutoComplete(email) : [];
  const showSuggestions =
    multiInvite &&
    composerFocused &&
    (email ?? '').trim().length > 0 &&
    suggestions.length > 0;

  const multiInviteComposer = (
    <div className={styles.composerWrap}>
      <div
        className={[
          styles.composer,
          composerFocused ? styles.composerFocused : '',
          helpMessage() ? styles.composerError : ''
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={focusComposerInput}
      >
        {pendingInvites.length > 0 && (
          <div className={styles.chipRow}>
            {pendingInvites.map(invite => {
              const matchedUser = findUserForInvite(invite);
              const label =
                invite.displayName && invite.displayName !== invite.email
                  ? invite.displayName
                  : invite.email;
              const memberDisabled = chipMemberOptionDisabled(invite);

              return (
                <div key={invite.key} className={styles.chip}>
                  <Avatar
                    member={{
                      name:
                        matchedUser?.display_name ||
                        matchedUser?.name ||
                        label,
                      email: invite.email,
                      role: invite.role,
                      id: invite.userId,
                      avatar: matchedUser?.avatar,
                      large_avatar_url: matchedUser?.large_avatar_url,
                      new_avatar_url: matchedUser?.new_avatar_url,
                      first_name: matchedUser?.first_name,
                      surname: matchedUser?.surname
                    }}
                    size="small"
                  />
                  <span className={styles.chipLabel} title={invite.email}>
                    {label}
                  </span>
                  <Select
                    className={styles.chipRole}
                    size="small"
                    value={invite.role}
                    onChange={(role: InviteRole) =>
                      updatePendingInviteRole(invite.key, role)
                    }
                    getPopupContainer={container}
                    dropdownMatchSelectWidth={false}
                    disabled={!isProjectActive}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}
                  >
                    <Select.Option value="guest">Client</Select.Option>
                    <Select.Option value="member" disabled={memberDisabled}>
                      {memberOptionLabel(memberDisabled)}
                    </Select.Option>
                    {collaboratorsEnabled && (
                      <Select.Option value="collaborator">
                        Collaborator
                      </Select.Option>
                    )}
                  </Select>
                  <Close
                    className={styles.chipRemove}
                    onMouseDown={event => event.preventDefault()}
                    onClick={event => {
                      event.stopPropagation();
                      removePendingInvite(invite.key);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.composerInputRow}>
          <Mail className={styles.mailIcon} />
          <input
            ref={composerInputRef}
            className={styles.composerNativeInput}
            placeholder={
              pendingInvites.length === 0
                ? 'Search or paste emails…'
                : 'Add another…'
            }
            value={email}
            disabled={!isProjectActive}
            onChange={event => onEmailChange(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            onFocus={handleComposerFocus}
            onBlur={handleComposerBlur}
            onClick={event => event.stopPropagation()}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && (
            <span className={styles.composerLoader}>
              <Loader size="small" />
            </span>
          )}
        </div>
      </div>

      {showSuggestions && (
        <div className={styles.suggestions} role="listbox">
          {suggestions.map(user => (
            <button
              key={user.email}
              type="button"
              className={styles.suggestionItem}
              onMouseDown={event => {
                event.preventDefault();
                selectSuggestedUser(user);
              }}
            >
              <Avatar member={user} {...{ autoComp: true }} size="small" />
              <span className={styles.suggestionText}>
                <span className={styles.autoCompName}>{user.display_name}</span>
                {user.display_name !== user.email && (
                  <span className={styles.autoCompEmail}>{user.email}</span>
                )}
              </span>
              <span className={styles.suggestionRole}>
                {user.role === 'guest'
                  ? 'Client'
                  : user.role === 'collaborator'
                  ? 'Collaborator'
                  : 'Member'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.inviteBox} id="inviteBox">
      <h3 className={styles.tabHeader}>
        <span id="elevioHelperMemberInvite">
          Share via email
        </span>
      </h3>
      {project && <p className={styles.inviteInfo}>{messaging}</p>}
      {!isProjectActive && (
        <Alert
          className={styles.upgradeMessage}
          type="warning"
          showIcon
          message={
            <span>
              Project has been disabled.{' '}
              <a
                href={`/projects/${project.id}/settings`}
                className={styles.planLink}
              >
                Enable Project
              </a>{' '}
              to invite members.
            </span>
          }
        />
      )}

      {multiInvite ? (
        <div className={styles.multiInviteLayout}>
          {multiInviteComposer}
          {helpMessage() && (
            <div className={styles.composerHelp}>{helpMessage()}</div>
          )}
          <div className={styles.sendRow}>
            <span className={styles.readyCount}>
              {pendingInvites.length === 0
                ? 'No one selected yet'
                : pendingInvites.length === 1
                ? '1 person ready'
                : `${pendingInvites.length} people ready`}
            </span>
            <Button
              className={styles.inviteButton}
              type="primary"
              size="large"
              loading={saving}
              disabled={!isProjectActive || totalToInvite === 0}
              onClick={() => sendMultiInvite()}
            >
              {pendingInvites.length <= 1
                ? 'Send invite'
                : `Send ${pendingInvites.length} invites`}
            </Button>
          </div>
        </div>
      ) : (
        <Form className={styles.emailInviteForm} onSubmit={sendEmail}>
          <Form.Item
            className={styles.emailField}
            hasFeedback
            validateStatus={getIsValid ? undefined : 'error'}
            help={helpMessage()}
          >
            <Input
              prefix={<Mail className={styles.mailIcon} />}
              suffix={
                email && getIsValid ? (
                  <Close
                    className={styles.closeIcon}
                    onClick={() => {
                      setEmail('');
                      setInputError(null);
                      setIsValid(false);
                    }}
                  />
                ) : null
              }
              className={styles.inviteEmail}
              placeholder="Invite new users by email"
              size="large"
              value={email}
              onChange={e => onEmailChange(e.target.value)}
              disabled={!isProjectActive || canNoLongerInviteAnyone}
            />
          </Form.Item>
          {roleSelect}
          <Button
            className={styles.inviteButton}
            type="primary"
            size="large"
            htmlType="submit"
            loading={saving}
            disabled={!isProjectActive || isSingleInviteDisabled}
          >
            {existingUser ? 'Add' : 'Invite'}
          </Button>
        </Form>
      )}

      {showGuestToMemberWarning && (
        <Alert
          type="warning"
          className={styles.upgradeMessage}
          showIcon
          message={
            <span>
              {guestToMemberInvites.length > 0 ? (
                <>
                  Some selected users are currently <b>Clients</b>. To change
                  them to <b>Members</b>, go to{' '}
                </>
              ) : (
                <>
                  {existingUser?.display_name} is currently a <b>Client</b>. To
                  change them to a <b>Member</b>, go to{' '}
                </>
              )}
              <a
                href={`/settings/organizations/${orgId}/team`}
                className={styles.planLink}
              >
                Organization settings &gt; Team
              </a>
              .
            </span>
          }
        />
      )}
      {membersMax != null && seatsLeft <= 0 && (
        <Alert
          type="warning"
          className={styles.upgradeMessage}
          message={
            billingRights ? (
              <span>
                To invite more people,{' '}
                <a
                  href={`/organizations/${orgId}/subscription`}
                  className={styles.planLink}
                >
                  upgrade your plan
                </a>{' '}
                or{' '}
                <a
                  type="link"
                  href={`/organizations/${orgId}/subscription?showAdditionalMember=true`}
                  className={styles.planLink}
                >
                  add more members
                </a>
              </span>
            ) : (
              <span>
                To invite more people please contact the account owner to
                upgrade your plan or add more members.
              </span>
            )
          }
        />
      )}
      <div id="ssoContainer" className={styles.ssoContainer}>
        <ArrowUp
          className={styles.upgradeArrow}
          onClick={() => setPopoverVisible(v => !v)}
        />
        <div className={styles.upgradePopoverContainer}>
          <UpgradePopover
            children=""
            isSmallView={false}
            open={popoverVisible}
            onOpenChange={() => setPopoverVisible(false)}
            message={
              <span className={styles.upgradeMessage}>
                <p>
                  <b>
                    Need to get everyone in your large organisation set up with
                    BugHerd?
                  </b>{' '}
                  Single Sign-On (SSO) is the easiest and most secure way to do
                  it.
                </p>
                <p>
                  <br />
                  Available only on the Enterprise plan.
                </p>
                <br />
              </span>
            }
            placement="topRight"
            className={styles.upgradePopover}
            getContainer={() =>
              document.getElementById('ssoContainer') as HTMLElement
            }
            imageSrc={ssoPopoverHeaderImage}
            billingRights={true}
            headerContent="SSO for Bugherd"
            buttonText="Contact us for pricing"
            href="https://bugherd.typeform.com/to/OcoH14?typeform-source=bugherd.com"
          />
        </div>
        <span>
          Security important to your team?&nbsp;
          <a
            className={styles.ssoText}
            onClick={() => setPopoverVisible(v => !v)}
          >
            Single Sign-On (SSO) is now available
          </a>
        </span>
      </div>
    </div>
  );
};

export default Invite;
